"use client";

import { useMemo } from "react";
import useSWR from "swr";
import { placeById } from "@/lib/search/place-resolver";
// 검색 페이지는 ?place=STN-… 를 첫 렌더부터 풀어야 한다 — 역 사전을 즉시 등록한다(이 모듈은
// 검색 라우트 전용이라 공용 번들에 들어가지 않는다. lib/search/station-places 참고).
import "@/lib/search/station-places.eager";
import { haversineKm } from "@/lib/search/spot-cluster";
import { placeDisplayName, type PlaceLocale } from "@/lib/search/place-param";
import type { CountryLandmarks } from "@/app/api/places/by-country/route";

/** 고른 명소 한 곳 — 거리 기준점·지도 표시·알약 라벨에 쓴다. */
export interface PlacePoint {
  /** "wd:<QID>" (전 세계 명소) 또는 지명 사전 id */
  id: string;
  name: string;
  lat: number;
  lng: number;
}

/**
 * URL `lm` → 전 세계 명소 id. 09-23 일본 필터는 QID 만 실었다("Q1234") — 같은 위키데이터
 * 항목이라 접두사만 붙이면 그대로 풀린다(예전 링크 호환).
 */
export function toWorldId(id: string | null | undefined): string | null {
  if (!id) return null;
  return /^Q\d+$/.test(id) ? `wd:${id}` : id;
}

const fetchJson = <T,>(url: string): Promise<T> =>
  fetch(url).then((r) => {
    if (!r.ok) throw new Error(`${url} ${r.status}`);
    return r.json() as Promise<T>;
  });

const SWR_STATIC = { revalidateOnFocus: false, revalidateOnReconnect: false, dedupingInterval: 60 * 60 * 1000 };

/** 지도에 보이는 범위 */
export interface ViewBox {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
}

/** 중심 한 점 → 작은 기본 범위(지도가 아직 멈춘 적 없을 때). 서버가 종류별 최소 범위로 넓힌다. */
export function boxAround(center: { lat: number; lng: number }): ViewBox {
  return { ne_lat: center.lat, ne_lng: center.lng, sw_lat: center.lat, sw_lng: center.lng };
}

/**
 * 서버가 첫 화면의 지도 중심으로 미리 정해 둔 나라(10-05) — 첫 렌더부터 나라를 알아
 * `/api/places/country` 왕복 없이 바로 목록을 받는다. `lat`·`lng` 는 그 나라를 정한 기준점이다.
 */
export interface InitialLandmarkCountry {
  iso: string;
  lat: number;
  lng: number;
}

/**
 * 기준점에서 이 거리 안이면 서버가 정한 나라를 그대로 쓴다 — 첫 화면(지도가 처음 멈춘 범위
 * 포함)은 나라 조회를 하지 않는다. 사용자가 지도를 이보다 멀리 옮겨야 다시 묻는다.
 */
const SAME_COUNTRY_KM = 25;

/** 목록 상태 — loading: 아직 모름(알약은 그리고 시트 안에서 기다림), none: 확실히 없음 */
export type LandmarkListStatus = "loading" | "ready" | "none";

/**
 * 지도가 보고 있는 **나라**의 명소 전체(종류별 인지도 순, 09-28).
 *
 * 좌표마다 목록이 바뀌면 어색하다 — 도쿄를 보다가 오사카 명소를 고르고 싶을 수도 있다.
 * 그래서 좌표는 "어느 나라인가"를 정하는 데만 쓰고(≈10km 반올림), 목록은 나라·언어 한 벌을
 * 캐시한다. 같은 나라 안에서 지도를 끌면 목록은 그대로다.
 *
 * `initial` 이 있으면(10-05) 첫 화면은 그 나라로 바로 목록을 받는다(마운트 즉시). 나라 조회는
 * 지도가 기준점에서 멀어졌을 때만 하고, 그 답을 기다리는 동안은 처음 나라 목록을 유지한다.
 */
export function useNearbyLandmarks(
  view: ViewBox | null,
  locale: string,
  initial?: InitialLandmarkCountry | null,
) {
  const center = view
    ? { lat: (view.ne_lat + view.sw_lat) / 2, lng: (view.ne_lng + view.sw_lng) / 2 }
    : null;
  const nearInitial = !!initial && (!center || haversineKm(center, initial) <= SAME_COUNTRY_KM);
  const lat = center && !nearInitial ? center.lat.toFixed(1) : null;
  const lng = center && !nearInitial ? center.lng.toFixed(1) : null;
  const { data: country } = useSWR(
    lat && lng ? `/api/places/country?lat=${lat}&lng=${lng}` : null,
    fetchJson<{ iso: string | null }>,
    { ...SWR_STATIC, keepPreviousData: true },
  );
  // 조회 중(답 없음)이면 처음 나라를 잠정으로 — 같은 나라 안의 이동이 대부분이다
  const iso = nearInitial ? initial!.iso : country ? country.iso : (initial?.iso ?? null);
  const countryKnown = nearInitial || country !== undefined || !center;
  const { data } = useSWR(
    iso ? `/api/places/by-country?iso=${iso}&locale=${locale}` : null,
    fetchJson<CountryLandmarks>,
    { ...SWR_STATIC, keepPreviousData: true },
  );
  const lists = iso ? data : undefined;
  const status: LandmarkListStatus = !iso
    ? countryKnown
      ? "none"
      : "loading"
    : !lists
      ? "loading"
      : Object.values(lists).some((items) => items.length > 0)
        ? "ready"
        : "none";
  return { lists, iso, status };
}

/** 전 세계 명소 id → 이름·좌표(URL 에서 복원할 때). 사전 지명·빈 값은 null. */
export function useWorldPlacePoint(id: string | null, locale: string): PlacePoint | null {
  const worldId = toWorldId(id);
  const { data } = useSWR(
    worldId?.startsWith("wd:") ? `/api/places/suggest?id=${encodeURIComponent(worldId)}&locale=${locale}` : null,
    (url: string) =>
      fetchJson<{ places: { id: string; name: string; lat: number; lng: number }[] }>(url).then(
        (d) => d.places[0] ?? null,
      ),
    SWR_STATIC,
  );
  return data ? { id: data.id, name: data.name, lat: data.lat, lng: data.lng } : null;
}

/**
 * URL 의 명소 id(`lm`·`place`) → 이름·좌표. 전 세계 명소("wd:")는 서버에서, 지명 사전(역·동네·
 * 명소)은 브라우저 사전에서 바로 푼다. 도시·나라는 "명소"가 아니라 null(09-29 — 명소 선택을
 * URL 로 유지하면서 사전 지명도 `lm` 에 실을 수 있게 됨).
 */
export function usePlacePoint(id: string | null, locale: string): PlacePoint | null {
  const world = useWorldPlacePoint(id, locale);
  const dict = useMemo((): PlacePoint | null => {
    if (!id || toWorldId(id)?.startsWith("wd:")) return null;
    const p = placeById(id);
    if (!p?.center || p.kind === "city" || p.kind === "country") return null;
    return {
      id: p.id,
      name: placeDisplayName(p.id, locale as PlaceLocale) ?? p.label,
      lat: p.center.lat,
      lng: p.center.lng,
    };
  }, [id, locale]);
  return world ?? dict;
}
